/* eslint-disable no-underscore-dangle */
import { useMemo, useCallback, useState } from "react";
import { useHistory } from "react-router";
import { Helmet } from "react-helmet";
import { gql, useQuery } from "@apollo/client";

import { UserType } from "../../../../types/userType";
import Layout from "../../../../components/Layout";
import Table from "../../../../components/Table";
import { Heading } from "../../../../components/Type";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import Button from "../../../../components/Button";
import SearchInput from "../../../../components/SearchInput";
import styles from "../index.module.scss";
import DashboardNav from "../DashboardNav";
import Loader from "../../../../components/Loader";
import EmptyView from "../../../../components/Table/EmptyView";
import CreateNewUserButton from "../CreateNewUserButton";
import { DateCell, ProfileCell, TextCell } from "../../../../components/Table/TableCells";

const GET_USERS = gql`
  query Users(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    users(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      updated_at
      first_name
      last_name
      adp_auth_num
      organization {
        name
      }
      locations {
        id
        name
      }
      role {
        type
        name
      }
      clients_authorizers {
        id
      }
    }
    usersConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

type WhereType = {
  [key: string]:
    | string[]
    | { [key: string]: boolean | string | string[] }[]
    | string[];
};

const AdminDashboard = () => {
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string | undefined>();
  const history = useHistory();
  const [, setBoundaryError] = useState<Error | null>(null);
  const [userData, setUserData] = useState<{
    users: UserType[];
    count: number;
  }>({ users: [], count: 0 });
  const { data, loading, refetch } = useQuery(GET_USERS, {
    onCompleted: (data) => {
      setUserData({
        users: data.users,
        count: data.usersConnection.aggregate.count,
      });
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const variables: {
        limit: number;
        start: number;
        sort?: unknown;
        where?: WhereType;
        connectionWhere?: WhereType;
      } = {
        limit: pageSize,
        start: pageIndex * pageSize,
      };
      if (sortBy.length) {
        if (sortBy[0].id === "locations") {
          variables.sort = `locations.name:${sortBy[0].desc ? "DESC" : "ASC"}`;
        } else {
          variables.sort = `${sortBy[0].id}:${sortBy[0].desc ? "DESC" : "ASC"}`;
        }
      }

      const where: WhereType = { "role.type": ["authorizer", "motion_support"] };
      if (search) {
        const or: { [key: string]: boolean | string }[] = [];
        or.push({ first_name_contains: search });
        or.push({ last_name_contains: search });
        if (or.length) where._or = or;
      }

      variables.where = where;
      variables.connectionWhere = where;

      setPageSize(pageSize);
      refetch(variables).then((response) => {
        if (response.data) {
          setUserData({
            users: response.data.users,
            count: response.data.usersConnection.aggregate.count,
          });
        }
      });
    },
    [refetch, search]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => (
          <ProfileCell
            admin
            data={row.original}
            description={row.original.adp_auth_num}
          />
        ),
      },
      {
        Header: "Last Updated",
        accessor: "updated_at",
        Cell: ({ value }) => <DateCell value={value} />,
      },
      {
        Header: "No. Clients",
        accessor: "clients_authorizers",
        disableSortBy: true,
        Cell: ({ value }) => <TextCell value={value.length} />,
      },
      {
        Header: "Organization(s)",
        accessor: "organization",
        Cell: ({ value }) =>
          value?.name ? <TextCell value={value.name} /> : "-",
      },
    ],
    []
  );

  const getCurrentFilters = useMemo(() => {
    const currentFilters: { id: string; value: string }[] = [];
    if (search) {
      currentFilters.push({ id: "first_name", value: search });
    } else {
      currentFilters.filter((f) => f.id !== "first_name");
    }
    return currentFilters;
  }, [search]);

  const pageCount = data ? Math.ceil(data.usersConnection.aggregate.count / pageSize) : 0;

  const handleResetFilters = () => {
    setSearch("");
    const input = (document.getElementById("search-filter") as HTMLInputElement);
    if (input) input.value = "";
  };

  return (
    <>
      <Layout>
        <Helmet>
          <title>Motion Home</title>
        </Helmet>
        <div className={styles.headerRow}>
          <Heading size="h3" component="h1" className={styles.heading}>
            Dashboard
          </Heading>
          <Button
            primary
            icon={<ArrowRight />}
            onClick={() => history.push("/admin/client/new")}>
              Create new client file
          </Button>
          <CreateNewUserButton />
        </div>
        <div className={styles.nav}>
          <DashboardNav />
        </div>

        {loading && (
          <Loader
            heading="Loading authorizer list..."
            className={styles.loader}
          />
        )}
        <div className={loading ? styles.tableHide : styles.tableShow}>
          <div className={styles.filterRow}>
            <div className={styles.filterItem}>
              <SearchInput
                onChange={(value) => setSearch(value)}
                placeholder="Search by first or last name"
                loading={loading}
              />
            </div>
            <div>
              <Button
                secondary
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            </div>
          </div>
          <Table
            data={userData.users}
            columns={columns}
            pageCount={pageCount}
            fetchData={fetchData}
            total={userData.count}
            loading={loading}
            handleRowClick={(row) => {
              history.push(`/admin/user/${row.original.id}`);
            }}
            emptyView={
              <EmptyView
                message={
                  search
                    ? `No results found matching your search criteria.`
                    : "You do not have any clients on file yet."
                }
              >
                <Button primary onClick={() => history.push("/client/new")}>
                  Create new client file
                </Button>
              </EmptyView>
            }
            initialState={{ sortBy: [{ id: "first_name", desc: false }] }}
            filters={getCurrentFilters}
          />
        </div>
      </Layout>
    </>
  );
};

export default AdminDashboard;
